import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Palette, RotateCcw, Eye, EyeOff } from 'lucide-react';

/**
 * Node Format Editor - Customize colors for the three node style types
 * Used in Visual Editor for PathNav/Sitemap stories
 */

// Default node format configuration
export const DEFAULT_NODE_FORMAT = {
  // Start node (homepage) - always green
  start: {
    background: '#10b981',
    text: '#ffffff',
    border: '#10b981'
  },
  // Standard nodes (pages, sections) - dark indigo
  standard: {
    background: '#667eea',
    text: '#ffffff',
    border: '#5a67d8'
  },
  // Light nodes (H1, H2 headings) - lighter background, dark text
  light: {
    background: '#a5b4fc',
    text: '#1e293b',
    border: '#818cf8'
  },
  // Unbounded nodes (direct URL links) - no background
  unbounded: {
    background: 'transparent',
    text: '#667eea',
    border: '#667eea'
  },
  // Connection lines
  lines: {
    color: '#94a3b8',
    activeColor: '#667eea'
  }
};

// Color presets for quick styling
const COLOR_PRESETS = [
  {
    name: 'Default',
    colors: { ...DEFAULT_NODE_FORMAT }
  },
  {
    name: 'Ocean',
    colors: {
      start: { background: '#0891b2', text: '#ffffff', border: '#0891b2' },
      standard: { background: '#0284c7', text: '#ffffff', border: '#0369a1' },
      light: { background: '#7dd3fc', text: '#0c4a6e', border: '#38bdf8' },
      unbounded: { background: 'transparent', text: '#0284c7', border: '#0284c7' },
      lines: { color: '#94a3b8', activeColor: '#0284c7' }
    }
  },
  {
    name: 'Forest',
    colors: {
      start: { background: '#16a34a', text: '#ffffff', border: '#16a34a' },
      standard: { background: '#15803d', text: '#ffffff', border: '#166534' },
      light: { background: '#86efac', text: '#14532d', border: '#4ade80' },
      unbounded: { background: 'transparent', text: '#15803d', border: '#15803d' },
      lines: { color: '#94a3b8', activeColor: '#15803d' }
    }
  },
  {
    name: 'Purple',
    colors: {
      start: { background: '#9333ea', text: '#ffffff', border: '#9333ea' },
      standard: { background: '#7c3aed', text: '#ffffff', border: '#6d28d9' },
      light: { background: '#c4b5fd', text: '#3b0764', border: '#a78bfa' },
      unbounded: { background: 'transparent', text: '#7c3aed', border: '#7c3aed' },
      lines: { color: '#94a3b8', activeColor: '#7c3aed' }
    }
  },
  {
    name: 'Rose',
    colors: {
      start: { background: '#e11d48', text: '#ffffff', border: '#e11d48' },
      standard: { background: '#be123c', text: '#ffffff', border: '#9f1239' },
      light: { background: '#fda4af', text: '#881337', border: '#fb7185' },
      unbounded: { background: 'transparent', text: '#be123c', border: '#be123c' },
      lines: { color: '#94a3b8', activeColor: '#be123c' }
    }
  },
  {
    name: 'Amber',
    colors: {
      start: { background: '#d97706', text: '#ffffff', border: '#d97706' },
      standard: { background: '#b45309', text: '#ffffff', border: '#92400e' },
      light: { background: '#fcd34d', text: '#78350f', border: '#fbbf24' },
      unbounded: { background: 'transparent', text: '#b45309', border: '#b45309' },
      lines: { color: '#94a3b8', activeColor: '#b45309' }
    }
  },
  {
    name: 'Slate',
    colors: {
      start: { background: '#475569', text: '#ffffff', border: '#475569' },
      standard: { background: '#334155', text: '#ffffff', border: '#1e293b' },
      light: { background: '#cbd5e1', text: '#0f172a', border: '#94a3b8' },
      unbounded: { background: 'transparent', text: '#334155', border: '#334155' },
      lines: { color: '#94a3b8', activeColor: '#334155' }
    }
  },
  {
    name: 'Coral',
    colors: {
      start: { background: '#f97316', text: '#ffffff', border: '#f97316' },
      standard: { background: '#ea580c', text: '#ffffff', border: '#c2410c' },
      light: { background: '#fed7aa', text: '#7c2d12', border: '#fdba74' },
      unbounded: { background: 'transparent', text: '#ea580c', border: '#ea580c' },
      lines: { color: '#94a3b8', activeColor: '#ea580c' }
    }
  }
];

const STORAGE_KEY = 'visual_editor_node_format';

// Color input component - defined outside to avoid recreation on each render
const ColorInput = ({ label, value, onChange, showTransparent = false }) => (
  <div className="flex items-center gap-2">
    <label className="text-xs text-slate-600 w-20">{label}:</label>
    {value === 'transparent' ? (
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded border border-dashed border-slate-300 bg-white flex items-center justify-center text-xs text-slate-400">
          ∅
        </div>
        {showTransparent && (
          <button 
            onClick={() => onChange('#667eea')}
            className="text-xs text-blue-600 hover:underline"
          >
            Add color
          </button>
        )}
      </div>
    ) : (
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 rounded border border-slate-300 cursor-pointer"
        />
        <span className="text-xs text-slate-500 font-mono w-16">{value}</span>
        {showTransparent && (
          <button 
            onClick={() => onChange('transparent')}
            className="text-xs text-slate-500 hover:text-red-600"
            title="Remove background"
          >
            ✕
          </button>
        )}
      </div>
    )}
  </div>
);

const NodeFormatEditor = ({ 
  isOpen,
  onClose,
  nodeFormat,
  onFormatChange
}) => {
  const [showPreview, setShowPreview] = useState(true);
  
  if (!isOpen) return null;
  
  const updateNodeStyle = (styleType, property, value) => {
    const updated = {
      ...nodeFormat,
      [styleType]: {
        ...nodeFormat[styleType],
        [property]: value
      }
    };
    onFormatChange(updated);
  };
  
  const updateLineStyle = (property, value) => {
    const updated = {
      ...nodeFormat,
      lines: {
        ...nodeFormat.lines,
        [property]: value
      }
    };
    onFormatChange(updated);
  };
  
  const applyPreset = (preset) => {
    onFormatChange(preset.colors);
  };
  
  const resetToDefault = () => {
    onFormatChange(DEFAULT_NODE_FORMAT);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Palette className="w-5 h-5 text-white" />
            <div>
              <h3 className="text-lg font-bold text-white">Node Format Editor</h3>
              <p className="text-sm text-white/80">Customize colors for each node type</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Quick Presets */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-slate-700">Quick Presets</label>
              <button 
                onClick={resetToDefault}
                className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
              >
                <RotateCcw className="w-3 h-3" />
                Reset to Default
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {COLOR_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => applyPreset(preset)}
                  className="p-2 rounded-lg border-2 border-slate-200 hover:border-slate-400 transition-all"
                >
                  <div className="flex gap-1 mb-1">
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: preset.colors.start.background }}
                    />
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: preset.colors.standard.background }}
                    />
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: preset.colors.light.background }}
                    />
                  </div>
                  <span className="text-xs font-medium text-slate-700">{preset.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Node Style Sections */}
          <div className="grid grid-cols-2 gap-6">
            {/* Start Node */}
            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
              <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                Start Node (Homepage)
              </h4>
              <div className="space-y-2">
                <ColorInput 
                  label="Background"
                  value={nodeFormat.start.background}
                  onChange={(v) => updateNodeStyle('start', 'background', v)}
                />
                <ColorInput 
                  label="Text"
                  value={nodeFormat.start.text}
                  onChange={(v) => updateNodeStyle('start', 'text', v)}
                />
              </div>
            </div>

            {/* Standard Node */}
            <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
              <h4 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-indigo-500" />
                Standard (Pages)
              </h4>
              <div className="space-y-2">
                <ColorInput 
                  label="Background"
                  value={nodeFormat.standard.background}
                  onChange={(v) => updateNodeStyle('standard', 'background', v)}
                />
                <ColorInput 
                  label="Text"
                  value={nodeFormat.standard.text}
                  onChange={(v) => updateNodeStyle('standard', 'text', v)}
                />
              </div>
            </div>

            {/* Light Node */}
            <div className="bg-violet-50 rounded-lg p-4 border border-violet-200">
              <h4 className="font-semibold text-violet-800 mb-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-violet-300" />
                Light (H1/H2 Headings)
              </h4>
              <div className="space-y-2">
                <ColorInput 
                  label="Background"
                  value={nodeFormat.light.background}
                  onChange={(v) => updateNodeStyle('light', 'background', v)}
                />
                <ColorInput 
                  label="Text"
                  value={nodeFormat.light.text}
                  onChange={(v) => updateNodeStyle('light', 'text', v)}
                />
              </div>
            </div>

            {/* Unbounded Node */}
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded border-2 border-dashed border-slate-400" />
                Unbounded (Direct Links)
              </h4>
              <div className="space-y-2">
                <ColorInput 
                  label="Background"
                  value={nodeFormat.unbounded.background}
                  onChange={(v) => updateNodeStyle('unbounded', 'background', v)}
                  showTransparent={true}
                />
                <ColorInput 
                  label="Text"
                  value={nodeFormat.unbounded.text}
                  onChange={(v) => updateNodeStyle('unbounded', 'text', v)}
                />
              </div>
            </div>
          </div>

          {/* Connection Lines */}
          <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="font-semibold text-gray-800 mb-3">Connection Lines</h4>
            <div className="flex gap-6">
              <ColorInput 
                label="Default"
                value={nodeFormat.lines.color}
                onChange={(v) => updateLineStyle('color', v)}
              />
              <ColorInput 
                label="Active"
                value={nodeFormat.lines.activeColor}
                onChange={(v) => updateLineStyle('activeColor', v)}
              />
            </div>
          </div>

          {/* Preview */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-slate-700">Preview</label>
              <button 
                onClick={() => setShowPreview(!showPreview)}
                className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
              >
                {showPreview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {showPreview ? 'Hide' : 'Show'}
              </button>
            </div>
            
            {showPreview && (
              <div className="bg-slate-100 rounded-lg p-6 border border-slate-200">
                <div className="flex items-center gap-4 flex-wrap">
                  {/* Start Node Preview */}
                  <div 
                    className="px-4 py-2 rounded-lg font-medium text-sm shadow-sm"
                    style={{ 
                      backgroundColor: nodeFormat.start.background, 
                      color: nodeFormat.start.text 
                    }}
                  >
                    Home
                  </div>
                  
                  {/* Connection line */}
                  <div 
                    className="w-8 h-0.5"
                    style={{ backgroundColor: nodeFormat.lines.color }}
                  />
                  
                  {/* Standard Node Preview */}
                  <div 
                    className="px-4 py-2 rounded-lg font-medium text-sm shadow-sm"
                    style={{ 
                      backgroundColor: nodeFormat.standard.background, 
                      color: nodeFormat.standard.text 
                    }}
                  >
                    About Page
                  </div>
                  
                  {/* Connection line */}
                  <div 
                    className="w-8 h-0.5"
                    style={{ backgroundColor: nodeFormat.lines.activeColor }}
                  />
                  
                  {/* Light Node Preview */}
                  <div 
                    className="px-4 py-2 rounded-lg font-medium text-sm shadow-sm"
                    style={{ 
                      backgroundColor: nodeFormat.light.background, 
                      color: nodeFormat.light.text 
                    }}
                  >
                    Section H2
                  </div>
                  
                  {/* Unbounded Node Preview */}
                  <div 
                    className="px-4 py-2 rounded-lg text-sm italic border border-dashed"
                    style={{ 
                      backgroundColor: nodeFormat.unbounded.background === 'transparent' ? 'transparent' : nodeFormat.unbounded.background,
                      color: nodeFormat.unbounded.text,
                      borderColor: nodeFormat.unbounded.text + '60'
                    }}
                  >
                    Visit Page →
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex justify-between items-center bg-slate-50">
          <p className="text-xs text-slate-500">
            Changes are applied immediately and saved automatically
          </p>
          <Button onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

// Hook to manage node format state with localStorage persistence
export const useNodeFormat = () => {
  const [nodeFormat, setNodeFormat] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return { ...DEFAULT_NODE_FORMAT, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error('Error loading node format:', e);
    }
    return DEFAULT_NODE_FORMAT;
  });
  
  const updateNodeFormat = (newFormat) => {
    setNodeFormat(newFormat);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newFormat));
    } catch (e) {
      console.error('Error saving node format:', e);
    }
  };
  
  return [nodeFormat, updateNodeFormat];
};

export default NodeFormatEditor;
